import { useCallback, useMemo, useRef } from "react";
import { isEqual } from "@common/helpers/comparison";
import { ViewDensity, View } from "@common/types/views/views";
import { DensityRow } from "@design-system/SettingsPanel/DensityRow";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { ViewConfig } from "@lib/views";
import {
    ColumnSettingsList,
    type ColumnSettingsListCopy,
} from "./ColumnSettingsList";
import { ColumnSettingsTrigger } from "./ColumnSettingsTrigger";

const TABLE_COLUMNS_COPY: ColumnSettingsListCopy = {
    shownTitle: "Shown in table",
    hiddenTitle: "Hidden in table",
    noneShown: "Drag columns here to show them",
    noneHidden: "All columns are visible",
};

// ViewTable's row height falls back to this when the view sets no density.
const TABLE_DEFAULT_DENSITY = ViewDensity.SM;

const DENSITY_OPTIONS = [
    { value: ViewDensity.SM, label: "Low" },
    { value: ViewDensity.MD, label: "Medium" },
    { value: ViewDensity.LG, label: "High" },
];

/** The columns without their widths, which the gear doesn't set. */
const withoutSizes = (columns: View["columns"]) =>
    columns.map((column) => ({
        ...column,
        options: { ...column.options, size: undefined },
    }));

interface Props<T extends View = View> {
    readonly view: T;
    readonly viewConfig: ViewConfig<any, string, string>;
    readonly defaultView: T;
    readonly onViewChange: (view: T) => void;
    readonly disabled?: boolean;
    readonly portalContainer?: HTMLElement;
    /** For tables whose rows are not driven by the view density. */
    readonly isDensityHidden?: boolean;
}

/**
 * A table's column gear: show, hide, pin and reorder the view's columns, set
 * the row density, and reset the columns to the default view's.
 */
export const ColumnSettings = <T extends View = View>({
    view,
    viewConfig,
    defaultView,
    onViewChange,
    disabled,
    portalContainer,
    isDensityHidden = false,
}: Props<T>) => {
    const { viewDensity: viewDensityFlag } = useTypedFlags();
    const isDensityShown = viewDensityFlag && !isDensityHidden;

    const scrollableContentRef = useRef<HTMLDivElement>(null);

    const hasColumnsChanged = useMemo(
        () =>
            !isEqual(
                withoutSizes(view.columns),
                withoutSizes(defaultView.columns),
            ),
        [view.columns, defaultView.columns],
    );

    const handleColumnsChange = useCallback(
        (columns: View["columns"]) => onViewChange({ ...view, columns }),
        [view, onViewChange],
    );

    const handleDensityChange = useCallback(
        (density: ViewDensity) => {
            onViewChange({ ...view, options: { ...view.options, density } });
        },
        [view, onViewChange],
    );

    const handlePinColumn = useCallback(
        (columnId: string, pin: "left" | "right" | undefined) => {
            const newColumns = view.columns.map((col) =>
                col.id === columnId
                    ? {
                          ...col,
                          options: {
                              ...col.options,
                              pin,
                          },
                      }
                    : col,
            );

            onViewChange({ ...view, columns: newColumns });
        },
        [view, onViewChange],
    );

    const handleReset = useCallback(() => {
        // Only reset columns, preserve filters and other view properties
        onViewChange({
            ...view,
            options: {
                ...view.options,
                ...defaultView.options,
            },
            columns: defaultView.columns,
        });
    }, [onViewChange, view, defaultView]);

    return (
        <SettingsPanel.Root
            trigger={
                <ColumnSettingsTrigger
                    hasChanges={hasColumnsChanged}
                    disabled={disabled}
                />
            }
            triggerTooltip="Change and rearrange visible columns"
            footer={
                hasColumnsChanged && (
                    <SettingsPanel.Action onClick={handleReset}>
                        Reset
                    </SettingsPanel.Action>
                )
            }
            scrollContainerRef={scrollableContentRef}
            portalContainer={portalContainer}
        >
            <ColumnSettingsList
                columns={view.columns}
                columnConfigs={viewConfig.columns}
                onColumnsChange={handleColumnsChange}
                copy={TABLE_COLUMNS_COPY}
                onPin={handlePinColumn}
                scrollContainerRef={scrollableContentRef}
            />
            {isDensityShown && (
                <DensityRow
                    options={DENSITY_OPTIONS}
                    value={view.options?.density ?? TABLE_DEFAULT_DENSITY}
                    onChange={handleDensityChange}
                />
            )}
        </SettingsPanel.Root>
    );
};
